/**
 * What is waiting, and what the site is doing.
 *
 * Four questions someone opening the panel actually has: is the site alright,
 * has anybody asked me for something, what needs a person, and what was I in
 * the middle of. The last two come from one request, `GET /api/attention`,
 * which the API computes across every collection and keeps for ten minutes.
 */
import React, { useEffect, useState } from "react";

import { api } from "../api.js";
import { BuildStatus, Message, Pill } from "../components/bits.jsx";
import { go } from "../app.jsx";
import { collectionBySlug } from "../../shared/collections.js";
import { formatDateTime, formatDay } from "../../shared/dates.js";

export default function Dashboard({ session, may }) {
  const [pending, setPending] = useState(null);
  // One answer for what needs a person and what is unfinished: the API
  // computes it from every collection and keeps it for ten minutes, so this
  // screen costs one request rather than one per collection.
  const [attention, setAttention] = useState(null);
  const [attentionError, setAttentionError] = useState(null);

  useEffect(() => {
    api.get("pending").then(setPending).catch(() => setPending({ pending: [] }));
    api
      .get("attention")
      .then(setAttention)
      .catch(() => setAttentionError("Could not work out what needs attention. The lists still work."));
  }, []);

  const drafts = attention?.drafts || null;
  const groups = attention?.groups || [];
  const collectionName = (slug) => collectionBySlug(slug)?.many.toLowerCase() || slug;

  return (
    <>
      <div className="head">
        <div>
          <h1>Hello, {session.user.displayName.split(" ")[0]}</h1>
          <p className="sub">The website's admin panel.</p>
        </div>
      </div>

      <div className="card">
        <header>
          <h2>The live site</h2>
          <p className="help">allbrainsclinic.com, and what the last change to it did.</p>
        </header>
        <div className="body">
          <BuildStatus branch="main" live="https://www.allbrainsclinic.com/" />
        </div>
      </div>

      {may("publish") && (
        <div className="card">
          <header>
            <h2>Waiting for a Publisher</h2>
            <p className="help">Drafts somebody has asked to have published.</p>
          </header>
          <div className="body" style={{ padding: pending?.pending?.length ? 0 : undefined }}>
            {!pending ? (
              <div className="empty">Loading…</div>
            ) : !pending.pending.length ? (
              <p className="hint">Nothing waiting.</p>
            ) : (
              <table>
                <tbody>
                  {pending.pending.map((request) => (
                    <tr key={`${request.collection}:${request.id}`} style={{ cursor: "pointer" }} onClick={() => go(`${request.collection}/${request.id}`)}>
                      <td>
                        <strong>{request.name}</strong>
                        <div className="hint">
                          {request.collection} · asked by {request.by}
                          {request.note ? ` — “${request.note}”` : ""}
                        </div>
                      </td>
                      <td>
                        <Pill status={request.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      <div className="card">
        <header>
          <h2>Needs attention</h2>
          <p className="help">
            Live reviews without a consent date, questions no page claims or that are asked twice, postings past their
            closing date or still to come. Each line opens the list showing exactly those.
          </p>
        </header>
        <div className="body" style={{ padding: groups.length ? 0 : undefined }}>
          {attentionError ? (
            <Message level="error">{attentionError}</Message>
          ) : !attention ? (
            <div className="empty">Loading…</div>
          ) : !groups.length ? (
            <p className="hint">Nothing needs attention.</p>
          ) : (
            <table>
              <tbody>
                {groups.map((group) => (
                  <tr
                    key={`${group.collection}:${group.kind}`}
                    style={{ cursor: "pointer" }}
                    onClick={() => go(`${group.collection}?${group.query}`)}
                  >
                    <td>
                      <strong>{group.label}</strong>
                      <div className="hint">
                        {collectionName(group.collection)}
                        {group.rows.length ? ` — ${group.rows.slice(0, 3).map((r) => r.name).join(", ")}${group.rows.length > 3 ? ", …" : ""}` : ""}
                      </div>
                    </td>
                    <td className="mono" style={{ textAlign: "right" }}>
                      {group.count}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {attention && (
          <div className="body" style={{ paddingTop: "0.6rem" }}>
            <p className="hint">
              As of {formatDateTime(attention.computedAt)}, Vancouver time. Rechecked every ten minutes, and after every save
              from the panel.
            </p>
          </div>
        )}
      </div>

      <div className="card">
        <header>
          <h2>Drafts</h2>
          <p className="help">Not on the live site. Nobody sees these but you. Across every collection.</p>
        </header>
        <div className="body" style={{ padding: drafts?.length ? 0 : undefined }}>
          {attentionError ? (
            <p className="hint">Open a collection to see its drafts.</p>
          ) : !drafts ? (
            <div className="empty">Loading…</div>
          ) : !drafts.length ? (
            <p className="hint">No drafts.</p>
          ) : (
            <table>
              <tbody>
                {drafts.slice(0, 12).map((item) => (
                  <tr key={`${item.collection}:${item.id}`} style={{ cursor: "pointer" }} onClick={() => go(`${item.collection}/${item.id}`)}>
                    <td>
                      <strong>{item.name}</strong>
                      <div className="hint">{collectionName(item.collection)}</div>
                    </td>
                    <td className="mono">{formatDay(item.updated)}</td>
                  </tr>
                ))}
                {drafts.length > 12 && (
                  <tr>
                    <td colSpan={2} className="hint">
                      {drafts.length - 12} more —{" "}
                      {[...new Set(drafts.slice(12).map((d) => d.collection))].map((slug, i) => (
                        <React.Fragment key={slug}>
                          {i ? ", " : ""}
                          <a href={`#/${slug}?status=draft`}>{collectionName(slug)}</a>
                        </React.Fragment>
                      ))}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {session.credential === "none" && (
        <Message level="error">
          The panel has no GitHub credential, so it cannot save anything. An Admin has to install the
          GitHub App — see admin/README.md.
        </Message>
      )}
    </>
  );
}
